import { useMemo, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { CHART_INK, tone, useIsDarkMode } from '@/lib/chartTheme';
import { formatCompactMoney } from '@/lib/money';
import { financeColor } from '../lib/financeColors';
import { FinanceChartFilters, type FinanceChartFilterProps } from './FinanceChartFilters';
import { FinanceRefreshing } from './FinanceNotReady';

export interface RevenueSourceView {
  name: string;
  category?: string;
  amount: number;
  percentage: number;
  color?: string;
}

type CentreState = {
  title: string;
  subtitle: string;
};

export function RevenueBreakdownCard({
  sources,
  currency,
  range,
  onRangeChange,
  countryCode,
  onCountryCodeChange,
  availableCountryCodes = [],
  startDate,
  endDate,
  isLoading = false,
}: {
  sources: RevenueSourceView[];
  currency: string;
} & FinanceChartFilterProps) {
  const isDark = useIsDarkMode();
  const total = sources.reduce((sum, source) => sum + source.amount, 0);
  const [hoverCentre, setHoverCentre] = useState<CentreState | null>(null);
  const centre: CentreState = hoverCentre ?? {
    title: formatCompactMoney(total, currency, { withCode: false }),
    subtitle: 'Total',
  };

  const option = useMemo(() => {
    const primary = tone(CHART_INK.primary, isDark);
    const muted = tone(CHART_INK.muted, isDark);
    const surface = tone(CHART_INK.surface, isDark);

    return {
      backgroundColor: 'transparent',
      tooltip: {
        trigger: 'item',
        backgroundColor: surface,
        borderColor: tone(CHART_INK.grid, isDark),
        textStyle: { color: primary },
        valueFormatter: (value: number | string) => formatCompactMoney(Number(value), currency),
      },
      title: [
        {
          text: centre.title,
          left: 'center',
          top: '42%',
          textStyle: { color: '#20aee8', fontSize: 16, fontWeight: 600 },
        },
        {
          text: centre.subtitle,
          left: 'center',
          top: '54%',
          textStyle: { color: muted, fontSize: 11, fontWeight: 400 },
        },
      ],
      series: [
        {
          name: 'Revenue source',
          type: 'pie',
          radius: ['58%', '82%'],
          center: ['50%', '52%'],
          avoidLabelOverlap: true,
          label: { show: false },
          emphasis: { scale: true, scaleSize: 5 },
          itemStyle: { borderWidth: 0 },
          data: sources.map((source) => ({
            name: source.name,
            value: source.amount,
            percentage: source.percentage,
            itemStyle: {
              color: source.color ?? financeColor(source.category ?? source.name),
            },
          })),
        },
      ],
    };
  }, [centre.subtitle, centre.title, currency, isDark, sources]);

  return (
    <Card className="shadow-none">
      <CardHeader className="flex-row flex-wrap items-center justify-between gap-3 space-y-0 p-5 pb-1">
        <div>
          <p className="text-sm font-medium text-sky-400">Revenue Breakdown</p>
          <p className="text-xs text-muted-foreground">
            Gross Transaction Value by source ·{' '}
            <span className="font-medium text-foreground">
              {formatCompactMoney(total, currency)}
            </span>
          </p>
        </div>
        <FinanceChartFilters
          range={range}
          onRangeChange={onRangeChange}
          countryCode={countryCode}
          onCountryCodeChange={onCountryCodeChange}
          availableCountryCodes={availableCountryCodes}
          {...(startDate ? { startDate } : {})}
          {...(endDate ? { endDate } : {})}
          dateAriaLabel="Change Revenue Breakdown date range"
          countryAriaLabel="Change Revenue Breakdown creator country"
        />
      </CardHeader>
      <CardContent className="p-5 pt-2">
        <FinanceRefreshing isLoading={isLoading}>
          <div className="grid items-center gap-5 sm:grid-cols-[minmax(170px,0.8fr)_minmax(230px,1.2fr)]">
        <ReactECharts
          option={option}
          notMerge
          style={{ height: 230, width: '100%' }}
          onEvents={{
            mouseover: (params: { name?: string; value?: number; data?: { percentage?: number } }) => {
              if (params.name == null || params.value == null) return;
              const percentage = Number(params.data?.percentage ?? 0);
              setHoverCentre({
                title: formatCompactMoney(Number(params.value), currency, { withCode: false }),
                subtitle: `${params.name} · ${percentage.toFixed(1)}%`,
              });
            },
            globalout: () => setHoverCentre(null),
          }}
        />
        <div className="space-y-3">
          {sources.length === 0 ? (
            <p className="text-sm text-muted-foreground">No revenue source data for this period.</p>
          ) : (
            sources.map((source) => {
              const color = source.color ?? financeColor(source.category ?? source.name);
              return (
                <div key={source.name}>
                  <div className="mb-1 flex items-center justify-between gap-3 text-xs">
                    <span>{source.name}</span>
                    <span className="text-muted-foreground">
                      {source.percentage}%&nbsp; {formatCompactMoney(source.amount, currency)}
                    </span>
                  </div>
                  <div className="h-1.5 overflow-hidden rounded-full bg-muted">
                    <div
                      className="h-full rounded-full"
                      style={{
                        backgroundColor: color,
                        width: `${Math.min(source.percentage, 100)}%`,
                      }}
                    />
                  </div>
                </div>
              );
            })
          )}
        </div>
          </div>
        </FinanceRefreshing>
      </CardContent>
    </Card>
  );
}
